'use client';

import { useRef } from 'react';
import { routePath } from '@/lib/routes';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER, SectionTag } from './bits';
import { useChoreo } from './motion';

/**
 * Appendix — the trust mechanism under every claim on the page: numbered
 * sources for the bracketed refs, the "at the time of writing" disclaimer,
 * and the correction invite with the one-week promise (honesty rails).
 */
export default function AppendixSources({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelector('[data-appendix-card]'), { y: 20, opacity: 0, duration: 0.65 });
    tl.from(
      root.querySelectorAll('[data-source-row]'),
      { opacity: 0, x: -10, duration: 0.45, stagger: 0.07 },
      '-=0.3',
    );
    return tl;
  });

  return (
    <section ref={rootRef} className="pb-16 pt-16 md:pb-24 md:pt-[88px]">
      <div className={CONTAINER}>
        <SectionTag num="APPENDIX" label="SOURCES & DISCLAIMER" />
        <div
          data-appendix-card
          className="mt-6 max-w-[880px] rounded-ref border border-line-2 bg-surface px-5 py-2 md:px-[26px]"
        >
          {data.sources.map((source) => (
            <div
              key={source.n}
              data-source-row
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-line py-3"
            >
              <span className="w-7 flex-none font-jetbrains text-[11px] text-brand-600">
                [{source.n}]
              </span>
              <span className="font-jetbrains text-[11.5px] text-ink">{source.label}</span>
              <span className="ml-auto text-right font-jetbrains text-[10.5px] text-dim">
                {source.note}
              </span>
            </div>
          ))}
          <p className="pb-3 pt-3.5 text-[12.5px] leading-[1.65] text-pretty text-dim">
            {data.disclaimer.body} Found something out of date?{' '}
            <a
              href={routePath('contact')}
              className="font-semibold text-brand-600 hover:text-brand-700"
            >
              Tell us
            </a>{' '}
            &mdash; corrections ship within a week, dated.
          </p>
        </div>
      </div>
    </section>
  );
}
